import React, { useState, useEffect, useRef, useCallback } from 'react'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import {
  GlobalSepStyles, PageWrap, Topbar, Body,
  MainCol, StepsCard, StepHeader, StepInput, Feedback, ResetBtn,
  SideCol, SideHeader, SessionStats, SessionStat,
  HistoryList, HistoryEmpty, HistoryItem,
} from './style'
import FeedbackModal, { FeedbackExtra } from '../_shared/FeedbackModal'
import { useAudioUnlock, playErrorBeep, playWarningBeep, playSuccessBeep } from '../_shared/sounds'
import { useScannerInput, useIsCoarsePointer, HIDDEN_INPUT_STYLE } from '../_shared/scanner'
import { normalizeRastreio } from '../_shared/rastreio'

/**
 * Tela de SEPARAÇÃO
 *
 * UX: operador bipa um RASTREIO e em seguida um BOX (em par direto, sem
 * listagem prévia). O par é submetido em POST /conference/scan e o
 * histórico lateral mostra as últimas operações da sessão.
 *
 * Endpoint reutilizado: POST /api/santolog/conference/scan
 * Mesmo do fluxo de Conferência — backend não distingue.
 */
function fmtTime(d) {
  if (!(d instanceof Date)) d = new Date(d)
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export default function SantoLogSeparacao(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  // Estado do par rastreio + box
  const [step, setStep] = useState('rastreio')           // 'rastreio' | 'box'
  const [rastreio, setRastreio] = useState('')
  const [boxBarcode, setBoxBarcode] = useState('')
  const [busy, setBusy] = useState(false)
  const [feedback, setFeedback] = useState({ kind: null, message: '' })
  // Box(es) esperado(s) pro rastreio bipado, retornado por GET /volume/by-rastreio.
  // Pode ser null (ainda não consultado), [] (volume sem box vinculado) ou
  // [{id, name, barcode}] / múltiplos boxes se o motorista tiver mais de um.
  const [expectedBoxes, setExpectedBoxes] = useState(null)
  const [expectedOrder, setExpectedOrder] = useState(null)

  const rastreioInputRef = useRef(null)
  const boxInputRef = useRef(null)

  // Histórico
  const [hist, setHist] = useState([])
  const okCount = hist.filter(h => h.kind === 'ok').length
  const errCount = hist.length - okCount

  // Modal de feedback (error / warning / success)
  const initialModal = { open: false, kind: 'error', title: '', message: '', correct_box: null, order: null }
  const [modal, setModal] = useState(initialModal)
  const modalOpenRef = useRef(false)
  useEffect(() => { modalOpenRef.current = modal.open }, [modal.open])

  // Destrava o AudioContext compartilhado no primeiro gesture
  useAudioUnlock()

  // Scanner: arma o input ATIVO (alterna conforme step) sem abrir teclado virtual.
  // armKey=step → re-arma quando troca rastreio↔box (evita teclado abrir na troca)
  const isCoarse = useIsCoarsePointer()
  const { arm: armScanner } = useScannerInput({
    getInput: () => step === 'rastreio' ? rastreioInputRef.current : boxInputRef.current,
    getDisabled: () => auth !== 'authorized' || modalOpenRef.current,
    armKey: step,
  })

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line
  // Foco inicial — usa arm() pra evitar abrir teclado no mobile
  useEffect(() => {
    if (auth !== 'authorized') return
    const t = setTimeout(armScanner, 120)
    return () => clearTimeout(t)
  }, [auth, step, armScanner])

  // Foco perpétuo: se perder foco, volta pro input ativo via arm() (sem teclado)
  const handleBlur = useCallback(() => {
    setTimeout(() => {
      if (modalOpenRef.current) return
      const ae = document.activeElement
      if (ae && ae !== document.body) {
        const tag = ae.tagName
        if (tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return
      }
      armScanner()
    }, 50)
  }, [armScanner])

  function pushHistory(entry) {
    setHist(prev => [{ ...entry, at: new Date() }, ...prev].slice(0, 50))
  }

  // ── HANDLERS ──────────────────────────────────────────────────────────────
  async function onRastreioEnter() {
    // Normaliza qualquer prefixo (SLEXPRESSO, TMRAPIDO, etc) para ALEF{NF}V{VOL}
    const code = normalizeRastreio(rastreio) || ''
    if (!code) return
    setRastreio(code) // mantém pra mostrar no preview
    setStep('box')
    setFeedback({ kind: null, message: 'Buscando volume…' })
    setBusy(true)
    setExpectedBoxes(null)
    setExpectedOrder(null)
    try {
      const res = await apiSantoLog.get(`/volume/by-rastreio/${encodeURIComponent(code)}`, { params: { module: 'separacao' } })
      const data = res.data?.data || res.data || {}
      const boxes = Array.isArray(data.expected_boxes) ? data.expected_boxes
                  : Array.isArray(data.boxes)          ? data.boxes
                  : data.expected_box                   ? [data.expected_box]
                  : []
      setExpectedBoxes(boxes)
      // order pode vir solto OU dentro de data.volume — tentamos os dois
      setExpectedOrder(data.order || data.volume?.order || null)
      if (boxes.length === 0) {
        setFeedback({ kind: 'warn', message: 'Volume sem box definido — bipe o box destino' })
        playWarningBeep()
      } else if (boxes.length === 1) {
        setFeedback({ kind: 'ok', message: `Box esperado: ${boxes[0].name}` })
      } else {
        setFeedback({ kind: 'ok', message: `${boxes.length} boxes vinculados — bipe o destino` })
      }
    } catch (err) {
      const status = err?.response?.status
      const raw = err?.response?.data || {}
      const errMsg = raw.error || raw.message || err.message
      if (status === 404) {
        playErrorBeep()
        setModal({ open: true, kind: 'error', title: 'Rastreio não encontrado', message: errMsg || `Volume "${code}" não localizado` })
        setFeedback({ kind: 'err', message: 'Rastreio não encontrado' })
        pushHistory({ kind: 'err', rastreio: code, box: '—', message: 'Rastreio não encontrado' })
        resetPair()
        return
      }
      // Outros erros: deixa seguir pro passo 2, o POST /conference/scan revalida
      setExpectedBoxes([])
      setFeedback({ kind: 'warn', message: 'Não foi possível consultar o box — siga e bipe normalmente' })
    } finally {
      setBusy(false)
      setTimeout(armScanner, 60)
    }
  }

  async function onBoxEnter() {
    const boxCode = boxBarcode.trim()
    const rastreioCode = rastreio.trim()
    if (!boxCode || !rastreioCode) return

    setBusy(true)
    setFeedback({ kind: null, message: 'Conferindo…' })

    try {
      // SEM validação client-side do box: manda o par direto e deixa o backend
      // resolver o box (pelo `barcode`), validar o vínculo e registrar na auditoria.
      // O endpoint é compartilhado com a Conferência; `module: 'separacao'` distingue.
      const res = await apiSantoLog.post('/conference/scan', {
        barcode: boxCode,
        rastreio: rastreioCode,
        module: 'separacao',
      })
      const data = res.data || {}
      const orderStatus = data.order_status
      const boxName = data.box?.name || data.box_name || boxCode

      if (data.already_coletado) {
        playWarningBeep()
        setFeedback({ kind: 'ok', message: `⚠ ${rastreioCode} já estava conferido` })
        pushHistory({ kind: 'ok', rastreio: rastreioCode, box: boxName, message: 'Já conferido' })
      } else {
        playSuccessBeep()
        setFeedback({
          kind: 'ok',
          message: `✓ ${rastreioCode} → ${boxName}${orderStatus === 'coletado' ? ' · pedido completo!' : ''}`,
        })
        pushHistory({ kind: 'ok', rastreio: rastreioCode, box: boxName, message: orderStatus || 'separado' })
      }
      resetPair()
    } catch (err) {
      console.error('POST /conference/scan failed:', err)
      const raw = err?.response?.data || {}
      const data = raw.data || raw
      const correctBox = data.correct_box || data.actual_box || raw.correct_box || raw.actual_box || null
      const order = data.order || raw.order || null
      const errMsg = data.error || raw.error || data.message || raw.message || err.message
      const status = err?.response?.status

      if (status === 404) {
        // rastreio (ou box) não encontrado
        playErrorBeep()
        setModal({ open: true, kind: 'error', title: 'Não encontrado', message: errMsg || `Volume "${rastreioCode}" não localizado` })
        setFeedback({ kind: 'err', message: errMsg || 'Não encontrado' })
        pushHistory({ kind: 'err', rastreio: rastreioCode, box: boxCode, message: errMsg || 'Não encontrado' })
      } else if (status === 422 && correctBox) {
        // box errado — o corpo traz o box correto pra exibir no alerta
        playWarningBeep()
        setModal({
          open: true, kind: 'warning',
          title: 'Box errado',
          message: errMsg || 'Volume não pertence a este box',
          correct_box: correctBox, order,
        })
        setFeedback({ kind: 'err', message: errMsg || 'Box errado' })
        pushHistory({ kind: 'err', rastreio: rastreioCode, box: boxCode, message: errMsg || 'Box errado' })
      } else if (status === 422) {
        // outros 422 (ex.: volume já embarcado / já saiu)
        playWarningBeep()
        setModal({
          open: true, kind: 'warning',
          title: 'Não foi possível conferir',
          message: errMsg || 'Volume não pode ser conferido',
          order,
        })
        setFeedback({ kind: 'err', message: errMsg || 'Não foi possível conferir' })
        pushHistory({ kind: 'err', rastreio: rastreioCode, box: boxCode, message: errMsg || 'Bloqueado' })
      } else {
        playErrorBeep()
        setModal({ open: true, kind: 'error', title: 'Erro', message: 'Erro: ' + errMsg })
        setFeedback({ kind: 'err', message: 'Erro: ' + errMsg })
        pushHistory({ kind: 'err', rastreio: rastreioCode, box: boxCode, message: errMsg })
      }
      resetPair()
    } finally {
      setBusy(false)
    }
  }

  function resetPair() {
    setRastreio('')
    setBoxBarcode('')
    setExpectedBoxes(null)
    setExpectedOrder(null)
    setStep('rastreio')
    setTimeout(armScanner, 60)
  }

  function closeModal() {
    setModal(initialModal)
    setTimeout(armScanner, 30)
  }

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Separação">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalSepStyles />
          <PageWrap>
            <Topbar>
              <h2>📡 Separação</h2>
              <span className="crumb">Bipe o rastreio e o box em sequência pra conferir o volume</span>
            </Topbar>

            <Body>
              <MainCol>
                <StepsCard>
                  {step === 'rastreio' ? (
                    <>
                      <StepHeader>
                        <div className="step-num">1</div>
                        <h3>Bipar rastreio</h3>
                        <p>Bipe o código do volume pra começar.</p>
                      </StepHeader>
                      <StepInput
                        ref={rastreioInputRef}
                        value={rastreio}
                        onChange={e => setRastreio(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); onRastreioEnter() }
                          if (e.key === 'Escape') resetPair()
                        }}
                        onBlur={handleBlur}
                        placeholder="Bipe o rastreio do volume"
                        disabled={busy}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        style={isCoarse ? HIDDEN_INPUT_STYLE : undefined}
                      />
                    </>
                  ) : (
                    <>
                      {/* Banner com box(es) esperado(s) — fica NO TOPO, antes do par */}
                      {expectedBoxes !== null && (
                        <div
                          style={{
                            padding: '18px 16px',
                            borderRadius: 12,
                            border: expectedBoxes.length === 0
                              ? '1.5px solid #f59e0b'
                              : '1.5px solid #00e5a0',
                            background: expectedBoxes.length === 0
                              ? 'linear-gradient(135deg, rgba(245, 158, 11, 0.18), rgba(245, 158, 11, 0.08))'
                              : 'linear-gradient(135deg, rgba(0, 229, 160, 0.18), rgba(0, 229, 160, 0.06))',
                            boxShadow: expectedBoxes.length === 0
                              ? '0 0 0 1px rgba(245, 158, 11, 0.25) inset'
                              : '0 0 0 1px rgba(0, 229, 160, 0.25) inset',
                            fontFamily: "'DM Sans', sans-serif",
                            color: '#e8eef5',
                          }}
                        >
                          {expectedBoxes.length === 0 ? (
                            <>
                              <div style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: 1,
                                textTransform: 'uppercase',
                                color: '#f59e0b',
                                marginBottom: 6,
                                textAlign: 'center',
                              }}>
                                ⚠ Sem box vinculado
                              </div>
                              <div style={{ fontSize: 14, color: '#e8eef5', textAlign: 'center' }}>
                                O volume ainda não tem box esperado definido.
                              </div>
                              {expectedOrder?.driver_name && (
                                <div style={{ marginTop: 6, fontSize: 12, color: '#8a95a3', textAlign: 'center' }}>
                                  Motorista: <strong style={{ color: '#e8eef5' }}>{expectedOrder.driver_name}</strong>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <div style={{
                                fontSize: 11,
                                fontWeight: 700,
                                letterSpacing: 1,
                                textTransform: 'uppercase',
                                color: '#00e5a0',
                                marginBottom: 10,
                                textAlign: 'center',
                              }}>
                                📦 Box esperado
                              </div>
                              <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 10,
                                justifyContent: 'center',
                              }}>
                                {expectedBoxes.map((b, i) => (
                                  <span
                                    key={b.id || b.name || i}
                                    style={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <strong style={{
                                      fontSize: 42,
                                      lineHeight: 1.1,
                                      color: '#00e5a0',
                                      fontFamily: "'Space Mono', monospace",
                                      letterSpacing: 0.5,
                                    }}>
                                      {b.name}
                                    </strong>
                                    {b.barcode && (
                                      <span style={{
                                        fontSize: 16,
                                        color: '#8a95a3',
                                        fontFamily: "'Space Mono', monospace",
                                      }}>
                                        {b.barcode}
                                      </span>
                                    )}
                                  </span>
                                ))}
                              </div>
                              {expectedOrder && (expectedOrder.order_number || expectedOrder.driver_name) && (
                                <div style={{
                                  marginTop: 12,
                                  paddingTop: 10,
                                  borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                                  fontSize: 12,
                                  color: '#8a95a3',
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 14,
                                  justifyContent: 'center',
                                }}>
                                  {expectedOrder.order_number && (
                                    <span>NF <strong style={{ color: '#e8eef5' }}>{expectedOrder.order_number}</strong></span>
                                  )}
                                  {expectedOrder.driver_name && (
                                    <span>🚛 <strong style={{ color: '#e8eef5' }}>{expectedOrder.driver_name}</strong></span>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}

                      {/* Card de info do pedido: rastreio + motorista (esq) · sequência (dir, grande)
                          Peso ~20% menor que o card do BOX (42px → ~34px). */}
                      <div
                        style={{
                          marginTop: 14,
                          padding: '14px 18px',
                          borderRadius: 12,
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          background: 'rgba(255, 255, 255, 0.03)',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 16,
                          fontFamily: "'DM Sans', sans-serif",
                          color: '#e8eef5',
                        }}
                      >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                          <div style={{
                            fontSize: 22,
                            fontWeight: 700,
                            color: '#e8eef5',
                            fontFamily: "'Space Mono', monospace",
                            letterSpacing: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {rastreio}
                          </div>
                          <div style={{
                            fontSize: 15,
                            color: '#cbd5e1',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 6,
                          }}>
                            🚛 <strong style={{ color: '#e8eef5' }}>
                              {expectedOrder?.driver_name || 'SEM MOTORISTA'}
                            </strong>
                          </div>
                        </div>

                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          minWidth: 80,
                        }}>
                          <div style={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: 1,
                            textTransform: 'uppercase',
                            color: '#8a95a3',
                          }}>
                            Sequência
                          </div>
                          <div style={{
                            fontSize: 34,
                            lineHeight: 1.05,
                            fontWeight: 700,
                            color: '#e8eef5',
                            fontFamily: "'Space Mono', monospace",
                          }}>
                            {expectedOrder?.sequence_order ?? '—'}
                          </div>
                        </div>
                      </div>

                      <StepInput
                        ref={boxInputRef}
                        value={boxBarcode}
                        $kind={feedback.kind}
                        onChange={e => setBoxBarcode(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { e.preventDefault(); onBoxEnter() }
                          if (e.key === 'Escape') resetPair()
                        }}
                        onBlur={handleBlur}
                        placeholder="Bipe o código do box"
                        disabled={busy}
                        autoComplete="off"
                        autoCorrect="off"
                        autoCapitalize="off"
                        spellCheck={false}
                        style={isCoarse ? HIDDEN_INPUT_STYLE : { marginTop: 12 }}
                      />
                      <ResetBtn type="button" onClick={resetPair} disabled={busy}>
                        ← Recomeçar par
                      </ResetBtn>
                    </>
                  )}
                  <Feedback $kind={feedback.kind}>
                    {feedback.message || (busy ? 'Processando…' : 'Aguardando bipe…')}
                  </Feedback>
                </StepsCard>
              </MainCol>

              {/* SIDE COL — Histórico da sessão */}
              <SideCol>
                <SideHeader>
                  <h3>Sessão atual</h3>
                  <p>Histórico dos últimos pares bipados (volátil — perde ao recarregar)</p>
                </SideHeader>
                <SessionStats>
                  <SessionStat $variant="ok">
                    <div className="l">Separados</div>
                    <div className="v">{okCount}</div>
                  </SessionStat>
                  <SessionStat $variant="err">
                    <div className="l">Falhas</div>
                    <div className="v">{errCount}</div>
                  </SessionStat>
                </SessionStats>
                <HistoryList>
                  {hist.length === 0 ? (
                    <HistoryEmpty>
                      Nenhum bipe registrado ainda.<br />
                      Comece bipando um rastreio.
                    </HistoryEmpty>
                  ) : (
                    hist.map((h, i) => (
                      <HistoryItem key={i} $kind={h.kind}>
                        <span className="icon">{h.kind === 'ok' ? '✓' : '✗'}</span>
                        <div className="info">
                          <div className="rastreio">{h.rastreio}</div>
                          <div className="meta">→ {h.box} · {h.message}</div>
                        </div>
                        <span className="time">{fmtTime(h.at)}</span>
                      </HistoryItem>
                    ))
                  )}
                </HistoryList>
              </SideCol>
            </Body>
          </PageWrap>

          {/* MODAL genérico de feedback (error / warning / success) */}
          <FeedbackModal
            visible={modal.open}
            kind={modal.kind}
            title={modal.title}
            message={modal.message}
            onClose={closeModal}
          >
            {modal.correct_box && (
              <FeedbackExtra>
                <div className="lbl">Box correto</div>
                <div className="val">📦 {modal.correct_box.name}</div>
                {modal.correct_box.barcode && <div className="meta">código {modal.correct_box.barcode}</div>}
                {modal.order && (
                  <div className="meta">
                    NF {modal.order.order_number}
                    {modal.order.driver_name && ` · motorista ${modal.order.driver_name}`}
                  </div>
                )}
              </FeedbackExtra>
            )}
          </FeedbackModal>
        </>
      )}
    </Dashboard>
  )
}
